import {useEffect, useState} from "react";
import {MapPin, Sunrise, Sunset, Moon, Sparkles} from "lucide-react";
import {apiGet} from "@/lib/api";

const CITIES = {
  "Mumbai": [19.076, 72.877, 5.5],
  "Delhi": [28.613, 77.209, 5.5],
  "Bengaluru": [12.971, 77.594, 5.5],
  "Chennai": [13.082, 80.270, 5.5],
  "Kolkata": [22.572, 88.363, 5.5],
  "Dubai": [25.204, 55.270, 4.0],
  "Singapore": [1.352, 103.819, 8.0],
  "London": [51.507, -0.127, 0.0],
  "New York": [40.713, -74.006, -5.0],
  "Toronto": [43.651, -79.347, -5.0],
  "Sydney": [-33.868, 151.209, 10.0]
};

const WEEK = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function MonthView({date, city, tradition, onPick}) {
  const [m, setM] = useState(null);
  const [y, mo] = date.split("-").map(Number);
  useEffect(() => {
    const [lat, lon, tz] = CITIES[city];
    setM(null); apiGet("/panchang/month", {year: y, month: mo, lat, lon, tz, tradition}).then(setM).catch(() => setM(false));
  }, [y, mo, city, tradition]);
  if (m === null) return <div className="empty-pane"><p>Computing the month…</p></div>;
  if (m === false) return <div className="empty-pane"><p>Could not load this month.</p></div>;
  const lead = (new Date(y, mo - 1, 1).getDay() + 6) % 7;
  const todayIso = new Date().toISOString().slice(0, 10);
  return (
    <div className="pm-wrap" data-testid="panchang-month">
      <h2 className="pm-title">{new Date(y, mo - 1, 1).toLocaleDateString("en-GB", {month: "long", year: "numeric"})} <em>· {city}</em></h2>
      <div className="pm-grid">
        {WEEK.map(w => <div key={w} className="pm-wd">{w}</div>)}
        {Array.from({length: lead}).map((_, i) => <div key={`b${i}`} className="pm-blank"/>)}
        {m.days.map(d => (
          <button key={d.date} data-testid={`pm-day-${d.date}`} className={`pm-day${d.date === todayIso ? " today" : ""}${d.festivals.length ? " fest" : ""}${["Purnima", "Amavasya"].includes(d.tithi) ? " moon" : ""}`} onClick={() => onPick(d.date)}>
            <b>{Number(d.date.slice(8))}</b>
            <span>{d.tithi}</span>
            <small>{d.paksha} · {d.nakshatra}</small>
            {d.festivals.slice(0, 2).map(f => <i key={f}>{f}</i>)}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function Panchang() {
  const [view, setView] = useState("day");
  const [city, setCity] = useState("Mumbai");
  const [tradition, setTradition] = useState("Smarta");
  const [traditions, setTraditions] = useState(["Smarta","Vaishnava","Madhva","Shaiva","Shakta","Lingayat"]);
  const [date, setDate] = useState(() => new Date().toISOString().slice(0,10));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { apiGet("/panchang/traditions").then(setTraditions).catch(() => {}); }, []);

  useEffect(() => {
    if (view !== "day") return;
    setLoading(true);
    const [lat, lon, tz] = CITIES[city];
    apiGet("/panchang", {date, lat, lon, tz, tradition})
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [city, tradition, date, view]);
  const shiftMonth = (k) => { const [y, mo] = date.split("-").map(Number); const n = new Date(Date.UTC(y, mo - 1 + k, 1)); setDate(n.toISOString().slice(0, 10)); };

  const dt = new Date(date);

  return (
    <div className="panchang-page" data-testid="panchang-page">
      <div className="page-title">
        <div>
          <div className="eyebrow">HINDU PANCHANG</div>
          <h1>Begin the day<br/><em>with the right muhurta.</em></h1>
          <p>Daily Panchang computed from Swiss Ephemeris for your city, with observances tuned to your sampradaya.</p>
        </div>
      </div>

      <div className="panchang-controls">
        <label>City
          <select data-testid="panchang-city" value={city} onChange={e => setCity(e.target.value)}>
            {Object.keys(CITIES).map(c => <option key={c}>{c}</option>)}
          </select>
        </label>
        <label>Tradition
          <select data-testid="panchang-tradition" value={tradition} onChange={e => setTradition(e.target.value)}>
            {traditions.map(t => <option key={t}>{t}</option>)}
          </select>
        </label>
        <label>Date
          <input data-testid="panchang-date" type="date" value={date} onChange={e => e.target.value && setDate(e.target.value)}/>
        </label>
        <div className="pm-toggle" data-testid="panchang-view-toggle">
          <button data-testid="panchang-view-day" className={view === "day" ? "active" : ""} onClick={() => setView("day")}>Day</button>
          <button data-testid="panchang-view-month" className={view === "month" ? "active" : ""} onClick={() => setView("month")}>Month</button>
        </div>
        {view === "month" && <div className="pm-nav"><button data-testid="pm-prev" onClick={() => shiftMonth(-1)}>‹</button><button data-testid="pm-next" onClick={() => shiftMonth(1)}>›</button></div>}
      </div>

      {view === "month" ? <MonthView date={date} city={city} tradition={tradition} onPick={(d) => { setDate(d); setView("day"); }}/> : !data || loading ? (
        <div className="empty-pane"><p>{loading ? "Computing panchang…" : "Pick a city and tradition to begin."}</p></div>
      ) : (
        <>
          <div className="panchang-hero">
            <div>
              <small>{data.vaar.toUpperCase()} · {data.tradition.toUpperCase()}</small>
              <h1>{data.tithi.name} <em>{data.tithi.paksha}</em></h1>
              <p>{dt.toLocaleDateString("en-GB",{weekday:"long",day:"numeric",month:"long",year:"numeric"})} · <MapPin size={13} style={{display:"inline"}}/> {city}</p>
            </div>
            <div className="hero-day">{dt.getDate()}</div>
          </div>

          <div className="panchang-grid">
            <div className="panchang-cell"><div className="eyebrow">TITHI</div><strong data-testid="pcell-tithi">{data.tithi.name}</strong><small>{data.tithi.paksha} · #{data.tithi.number}</small></div>
            <div className="panchang-cell"><div className="eyebrow">NAKSHATRA</div><strong data-testid="pcell-nakshatra">{data.nakshatra.name}</strong><small>#{data.nakshatra.number} of 27</small></div>
            <div className="panchang-cell"><div className="eyebrow">YOGA</div><strong data-testid="pcell-yoga">{data.yoga.name}</strong><small>#{data.yoga.number} of 27</small></div>
            <div className="panchang-cell"><div className="eyebrow">KARANA</div><strong data-testid="pcell-karana">{data.karana.name}</strong><small>Half-tithi #{data.karana.number}</small></div>
            <div className="panchang-cell"><div className="eyebrow"><Sunrise size={11} style={{display:"inline"}}/> SUNRISE</div><strong>{data.sunrise}</strong><small>Local</small></div>
            <div className="panchang-cell"><div className="eyebrow"><Sunset size={11} style={{display:"inline"}}/> SUNSET</div><strong>{data.sunset}</strong><small>Local</small></div>
            <div className="panchang-cell"><div className="eyebrow"><Moon size={11} style={{display:"inline"}}/> MOONRISE</div><strong>{data.moonrise}</strong><small>Local</small></div>
            <div className="panchang-cell"><div className="eyebrow"><Moon size={11} style={{display:"inline"}}/> MOONSET</div><strong>{data.moonset}</strong><small>Local</small></div>
          </div>

          <div>
            <div className="eyebrow" style={{marginBottom:14}}>MUHURTAS</div>
            <div className="panchang-muhurtas">
              <div><span className="label">Abhijit (auspicious)</span><span className="value" data-testid="muhurta-abhijit">{data.muhurta.abhijit}</span></div>
              <div className="warn"><span className="label">Rahu Kaal (avoid)</span><span className="value" data-testid="muhurta-rahu">{data.muhurta.rahu_kaal}</span></div>
              <div className="warn"><span className="label">Yama Ganda (avoid)</span><span className="value">{data.muhurta.yama_ganda}</span></div>
              <div className="warn"><span className="label">Gulika Kaal (avoid)</span><span className="value">{data.muhurta.gulika}</span></div>
            </div>
          </div>

          {data.festivals.length > 0 && (
            <div>
              <div className="eyebrow" style={{marginBottom:14}}><Sparkles size={11} style={{display:"inline"}}/> OBSERVANCES</div>
              <div className="panchang-festivals">
                {data.festivals.map((f, i) => (
                  <div key={i} className="festival-item" data-testid={`festival-${i}`}>
                    <strong>{f.name}</strong>
                    <p>{f.note}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
